import { useState } from "react";
import useResettableState from "hooks/useResettableState";
import {
  Box,
  Button,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { useQuery } from "@tanstack/react-query";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { differenceInCalendarDays, format, isValid } from "date-fns";
import DashboardLayout from "layout";
import {
  BreakdownItem,
  FinanceQuery,
  Granularity,
  PeriodKey,
  ProductKey,
  getFinanceBreakdown,
  getFinanceSummary,
  getFinanceTrends,
} from "services/finance";
import FinanceStatCards from "./FinanceStatCards";
import BreakdownCard from "./BreakdownCard";
import TrendsChart from "./TrendsChart";
import ActivityTable from "./ActivityTable";
import AddExpenseDrawer from "./AddExpenseDrawer";
import AddRevenueDrawer from "./AddRevenueDrawer";
import CategoryDetailDrawer from "./CategoryDetailDrawer";

const PERIODS: { value: PeriodKey | "custom"; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "this_week", label: "This Week" },
  { value: "last_7_days", label: "Last 7 Days" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "last_30_days", label: "Last 30 Days" },
  { value: "this_year", label: "This Year" },
  { value: "custom", label: "Custom Range" },
];

const PRODUCTS: { value: ProductKey; label: string }[] = [
  { value: "all", label: "All Products" },
  { value: "flight", label: "Flights" },
  { value: "ride", label: "Rides" },
  { value: "general", label: "General" },
];

const selectSx = {
  bgcolor: "#FFFFFF",
  "& .MuiOutlinedInput-root": { borderRadius: "8px", height: 40 },
};

const Finance = () => {
  const [period, setPeriod] = useState<PeriodKey | "custom">("this_month");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const toApiDate = (d: Date | null) =>
    d && isValid(d) ? format(d, "yyyy-MM-dd") : "";
  const start = toApiDate(startDate);
  const end = toApiDate(endDate);
  const [product, setProduct] = useState<ProductKey>("all");
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [revenueOpen, setRevenueOpen] = useState(false);
  const [selectedLine, setSelectedLine] = useState<BreakdownItem | null>(null);

  const isCustom = period === "custom";
  const customReady = isCustom && start && end && start <= end;

  // Pick a sensible granularity whenever the date filter changes; the admin
  // can still switch it by hand afterwards. Derived in render (not an effect)
  // so the trends query doesn't fetch the old granularity first.
  let defaultGranularity: Granularity = "daily";
  if (period === "this_year") {
    defaultGranularity = "monthly";
  } else if (customReady) {
    const days = differenceInCalendarDays(endDate!, startDate!) + 1;
    defaultGranularity = days <= 62 ? "daily" : days <= 183 ? "weekly" : "monthly";
  }
  const [granularity, setGranularity] = useResettableState<Granularity>(
    defaultGranularity,
    `${period}|${start}|${end}`,
  );

  // Until both custom dates are picked, keep showing this month's data.
  const query: FinanceQuery = customReady
    ? { start, end, product }
    : { period: isCustom ? "this_month" : (period as PeriodKey), product };

  const summaryQuery = useQuery({
    queryKey: ["finance", "summary", query],
    queryFn: () => getFinanceSummary(query),
  });

  const breakdownQuery = useQuery({
    queryKey: ["finance", "breakdown", query],
    queryFn: () => getFinanceBreakdown(query),
  });


  // The chart covers the same window as the cards above it.
  const trendsParams = { ...query, granularity };
  const trendsQuery = useQuery({
    queryKey: ["finance", "trends", trendsParams],
    queryFn: () => getFinanceTrends(trendsParams),
  });

  const breakdown = breakdownQuery.data?.data;

  return (
    <DashboardLayout nextSideBar>
      <Box sx={{ width: "100%", p: { xs: 0, sm: 1 } }} display="flex" flexDirection="column" gap="24px">
        <Box
          display="flex"
          flexDirection={{ xs: "column", lg: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", lg: "flex-start" }}
          gap="16px"
        >
          <Box>
            <Typography fontSize={{ xs: 24, sm: 32 }} fontWeight={600} color="#101828">
              Finance
            </Typography>
            <Typography fontSize={14} color="#475467">
              Track revenue, expenses and profitability across WiaMove.
            </Typography>
          </Box>

          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            flexWrap="wrap"
            gap="12px"
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent={{ lg: "flex-end" }}
          >
            <TextField
              select
              size="small"
              value={period}
              onChange={(e) => setPeriod(e.target.value as PeriodKey | "custom")}
              sx={{ ...selectSx, minWidth: 170 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: "#101828" }} />
                  </InputAdornment>
                ),
              }}
            >
              {PERIODS.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </TextField>
            {isCustom && (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box display="flex" gap="8px">
                  <DatePicker
                    label="From"
                    value={startDate}
                    onChange={setStartDate}
                    maxDate={endDate || new Date()}
                    format="dd MMM yyyy"
                    slotProps={{
                      textField: { size: "small", sx: { ...selectSx, flex: 1, minWidth: 150 } },
                    }}
                  />
                  <DatePicker
                    label="To"
                    value={endDate}
                    onChange={setEndDate}
                    minDate={startDate || undefined}
                    maxDate={new Date()}
                    format="dd MMM yyyy"
                    slotProps={{
                      textField: { size: "small", sx: { ...selectSx, flex: 1, minWidth: 150 } },
                    }}
                  />
                </Box>
              </LocalizationProvider>
            )}
            <TextField
              select
              size="small"
              value={product}
              onChange={(e) => setProduct(e.target.value as ProductKey)}
              sx={{ ...selectSx, minWidth: 150 }}
            >
              {PRODUCTS.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </TextField>
            <Box display="flex" gap="12px">
              <Button
                variant="contained"
                onClick={() => setRevenueOpen(true)}
                sx={{
                  flex: { xs: 1, sm: "initial" },
                  bgcolor: "#2F6B4F",
                  height: 40,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  "&:hover": { bgcolor: "#245540" },
                }}
              >
                + Add Revenue
              </Button>
              <Button
                variant="outlined"
                onClick={() => setExpenseOpen(true)}
                sx={{
                  flex: { xs: 1, sm: "initial" },
                  color: "#2F6B4F",
                  borderColor: "#2F6B4F",
                  height: 40,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                + Add Expense
              </Button>
            </Box>
          </Box>
        </Box>

        {isCustom && !customReady && (
          <Typography fontSize={13} color="#B54708">
            Pick a start and end date to apply the custom range. Showing this month for now.
          </Typography>
        )}

        <FinanceStatCards
          summary={summaryQuery.data?.data}
          isLoading={summaryQuery.isLoading}
        />

        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
          gap="16px"
          alignItems="start"
        >
          <BreakdownCard
            title="Revenue Breakdown"
            subtitle="See where WiaMove revenue is coming from."
            group={breakdown?.revenue}
            color="#2F6B4F"
            isLoading={breakdownQuery.isLoading}
            emptyText="No revenue recorded for this period"
            onSelect={setSelectedLine}
          />
          <BreakdownCard
            title="Expense Breakdown"
            subtitle="Track where WiaMove operating costs are going."
            group={breakdown?.expenses}
            color="#D92D20"
            isLoading={breakdownQuery.isLoading}
            showTotal="Total Expenses"
            emptyText="No expenses recorded for this period"
            onSelect={setSelectedLine}
          />
        </Box>

        <TrendsChart
          series={trendsQuery.data?.data?.series || []}
          granularity={granularity}
          onGranularityChange={setGranularity}
          isLoading={trendsQuery.isLoading}
        />

        <ActivityTable query={query} />
      </Box>

      <AddExpenseDrawer open={expenseOpen} onClose={() => setExpenseOpen(false)} />
      <AddRevenueDrawer open={revenueOpen} onClose={() => setRevenueOpen(false)} />
      {selectedLine && (
        <CategoryDetailDrawer
          key={selectedLine.category}
          line={selectedLine}
          query={query}
          onClose={() => setSelectedLine(null)}
        />
      )}
    </DashboardLayout>
  );
};

export default Finance;
